/**
 * 実験（lesson-format.md §7.2）の予想と比較。純粋な関数だけを置く。
 */
import { normalizeOutput } from '../../lib/judge';
import type { Expect } from './types';
import type { RunOutcomeView } from './run-lesson';

export type Prediction =
  { kind: 'output'; text: string } | { kind: 'compile-error' } | { kind: 'runtime-error' };

/** 比べる相手（実際の実行結果か、Swift 6.2 で記録した結果） */
export type Observed =
  | { kind: 'ok'; stdout: string }
  | { kind: 'compile-error' }
  | { kind: 'runtime-error' }
  | { kind: 'timeout' };

/** 予想を出したか。空の出力欄だけでは実行できない */
export function canRun(prediction: Prediction | null): prediction is Prediction {
  if (prediction === null) return false;
  return prediction.kind !== 'output' || prediction.text.trim() !== '';
}

export function observedFromRun(v: RunOutcomeView): Observed {
  return v.kind === 'ok' ? { kind: 'ok', stdout: v.stdout } : { kind: v.kind };
}

export function observedFromExpect(e: Expect): Observed {
  return e.outcome === 'ok' ? { kind: 'ok', stdout: e.expected } : { kind: e.outcome };
}

export function predictionMatches(p: Prediction, o: Observed): boolean {
  if (p.kind === 'output') {
    return o.kind === 'ok' && normalizeOutput(p.text) === normalizeOutput(o.stdout);
  }
  return p.kind === o.kind;
}

export function outcomeLabel(kind: Observed['kind'] | Prediction['kind']): string {
  switch (kind) {
    case 'compile-error':
      return 'コンパイルエラー';
    case 'runtime-error':
      return '実行時エラー';
    case 'timeout':
      return '3 秒以内に終わりませんでした';
    case 'ok':
    case 'output':
      return '';
  }
}

/** 記録した期待と実際が食い違うか（実行環境の違いを知らせる） */
export function differsFromRecord(e: Expect, o: Observed): boolean {
  return !predictionMatches(
    e.outcome === 'ok' ? { kind: 'output', text: e.expected } : { kind: e.outcome },
    o,
  );
}
